<script>
import AnchorPanel from '@/components/editor/AnchorPanel';
import BackgroundPanel from '@/components/editor/BackgroundPanel';
import BlocksPanel from '@/components/editor/BlocksPanel';
import CodeBlockPanel from '@/components/editor/CodeBlockPanel';
import DashboardLayout from '@/components/layouts/dashboard/DashboardLayout';
import EditorToolbar from '@/components/editor/EditorToolbar';
import EditorGuide from '@/components/editor/EditorGuide';
import FileBrowser from '@/components/file-browser/FileBrowser';
import GalleryPanel from '@/components/editor/GalleryPanel';
import ImagePanel from '@/components/editor/ImagePanel';
import LinkPanel from '@/components/editor/LinkPanel';
import MetadataPanel from '@/components/editor/MetadataPanel';
import PublishProgress from '@/components/editor/PublishProgress';
import RepeatableToolbar from '@/components/editor/RepeatableToolbar';
import RevisionPanel from '@/components/editor/RevisionPanel';
import SidebarButtonGroup from '@/components/layouts/dashboard/SidebarButtonGroup';
import SidebarLink from '@/components/layouts/dashboard/SidebarLink';
import SidebarLinkGroup from '@/components/layouts/dashboard/SidebarLinkGroup';
import TableToolbar from '@/components/editor/TableToolbar';
import VideoPanel from '@/components/editor/VideoPanel';
import Editor from '@/modules/editor';
import escapeHtml from 'escape-html';
import getErrorMessage from '@/modules/server-errors';
import keyboardShortcut from '@/filters/keyboard-shortcut';
import { disableInteractions, repairNestedRegions, shimLegacyClasses, simulateClicks } from '@/modules/editor/frame.js';
import { getType } from '@/modules/editor/region-types.js';
import { prettyFilename } from '@/modules/prettifier';
import { dirname, extname, removeBasePath } from '@/modules/file-utilities';
import { sourceOnlyExtensions } from '@/modules/editor/file-types';
import URI from 'urijs';

export default {
  name: 'EditPage',
  components: {
    AnchorPanel,
    BackgroundPanel,
    BlocksPanel,
    CodeBlockPanel,
    DashboardLayout,
    EditorGuide,
    EditorToolbar,
    FileBrowser,
    GalleryPanel,
    ImagePanel,
    LinkPanel,
    MetadataPanel,
    PublishProgress,
    RepeatableToolbar,
    RevisionPanel,
    SidebarButtonGroup,
    SidebarLink,
    SidebarLinkGroup,
    TableToolbar,
    VideoPanel
  },
  filters: {
    keyboardShortcut
  },
  metaInfo() {
    return { title: this.title };
  },
  data() {
    return {
      title: '',
      activeEditor: null,
      blocks: [],
      cleanState: null,
      contentRegions: [],
      currentRevision: null,
      erroneousRegions: [],
      fileBrowserDialogVisible: false,
      iframeSrc: 'about:blank',
      isLoadingRevisions: false,
      isPageLoading: true,
      isPublishing: false,
      isUploading: false,
      link: {
        href: '',
        target: '',
        title: ''
      },
      metadata: {
        title: '',
        description: '',
        image: '',
        robots: false,
        type: '',
        url: ''
      },
      page: {},
      publishMessage: '',
      publishPercentage: 0,
      publishQueue: [],
      publishStatus: null,
      publishStatusAttempts: 0,
      revisions: [],
      showPublishQueue: false,
      site: {},
      statusCode: null,
      unpublishedRegions: [],
      upgradeRequiredDialogVisible: false
    };
  },
  computed: {
    // Determines if all user-generated metadata has been filled in
    isMetadataComplete() {
      return this.metadata.title && this.metadata.description && this.metadata.image;
    },

    // Determines the page's URL
    pageUrl() {
      const url = (this.site.url || '').replace(/\/+$/, '');
      const path = (this.page.path || '').replace(/^\/+/, '');
      return url + '/' + path;
    }
  },
  watch: {
    // When a revision is opened, we use $router.push to load it. However, if there's already one
    // open then $router.push won't load the new route (it just updates the params), so we need to
    // watch the route object and update the iframe manually.
    $route() {
      this.isPageLoading = true;
      this.iframeSrc = 'about:blank';
      this.$nextTick(() => {
        this.iframeSrc = `/api/sites/${this.$route.params.siteId}/pages/${this.$route.params.pageId}/revisions/${this.$route.params.revisionId}/render`;
      });
    }
  },
  created() {
    // Fetch initial data
    Promise.all([
      this.$http.get(`/sites/${this.$route.params.siteId}`),
      this.$http.get(`/sites/${this.$route.params.siteId}/pages/${this.$route.params.pageId}`),
      this.$http.get(`/sites/${this.$route.params.siteId}/blocks`, { params: { limit: 1000, sortBy: 'name' } }),
      this.$route.params.revisionId
        ? this.$http.get(
            `/sites/${this.$route.params.siteId}/pages/${this.$route.params.pageId}/revisions/${this.$route.params.revisionId}`
          )
        : null
    ])
      .then(res => {
        this.site = res[0].data.site;
        this.page = res[1].data.page;
        this.blocks = res[2].data.blocks;
        this.currentRevision = res[3] ? res[3].data.revision : null;
        this.title = this.page.name;
        this.iframeSrc = this.currentRevision
          ? `/api/sites/${this.site.id}/pages/${this.page.id}/revisions/${this.currentRevision.id}/render`
          : `/api/sites/${this.site.id}/pages/${this.page.id}/render`;
        this.fetchRevisions();
        this.statusCode = 200;

        // Source only pages should immediately redirect to the source editor
        if (sourceOnlyExtensions.includes(extname(this.page.path).toLowerCase())) {
          this.$router.push({ name: 'EditSource', params: { siteId: this.site.id, pageId: this.page.id } });
          return;
        }

        // Bump to the site view if the site is on lockdown
        if (this.site.isLocked) {
          this.$router.push({ name: 'Site', params: { siteId: this.site.id } });
          return;
        }
      })
      .catch(err => (this.statusCode = err.response.status));

    window.addEventListener('keydown', this.handlekeyboardShortcut);
  },
  beforeRouteLeave(to, from, next) {
    // Check for unsaved changes
    if (this.isDirty()) {
      this.$confirm(this.$t("Your changes haven't been saved. Are you sure you want to continue?"), {
        title: this.$t('Unsaved Changes'),
        confirmButtonText: this.$t('Continue'),
        cancelButtonText: this.$t('Cancel')
      }).then(() => next());
    } else {
      next();
    }
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.handlekeyboardShortcut);
  },
  methods: {
    editBackground(element) {
      this.$refs.backgroundPanel.show({ element });
    },

    editGallery(element) {
      this.$refs.galleryPanel.show({ element });
    },

    editImage(element) {
      this.$refs.imagePanel.show({ element });
    },

    getDuplicateIds(doc) {
      const elements = [...doc.querySelectorAll('[id]')];
      const ids = [];
      const dups = [];

      elements.map(el => (ids.includes(el.id) ? dups.push(el) : ids.push(el.id)));

      return dups;
    },

    handlePageLoad() {
      const iframe = this.$refs.pageFrame;
      const win = iframe.contentWindow;
      const doc = iframe.contentWindow.document;

      // Page load is called when about:blank loads and then when the actual page loads
      if (doc.location.href === 'about:blank') {
        return;
      }

      this.contentRegions = [];
      this.erroneousRegions = [];
      this.isPageLoading = false;
      this.metadata = this.parseMetadata();

      // Disable iframe interactions, apply shims, and simulate clicks on the main document
      disableInteractions(iframe);
      repairNestedRegions(iframe);
      shimLegacyClasses(iframe);
      simulateClicks(iframe, document);

      // Blur anything that was autofocused
      if (doc.activeElement) {
        doc.activeElement.blur();
      }

      // Show no regions notice
      if (![...doc.querySelectorAll('.cms-editable')].length) {
        this.makeClean();
        this.$notify({
          type: 'warning',
          title: this.$t('Warning'),
          message: this.$t("This page doesn't have any content regions."),
          duration: 5000
        });
      }

      // Check for duplicate ids
      this.getDuplicateIds(doc).map(element => {
        if (element.classList.contains('cms-editable')) {
          this.erroneousRegions.push({
            element,
            text: this.$t('Duplicate ID')
          });
        }
      });

      // Inject TinyMCE
      const script = doc.createElement('script');
      script.src = `${location.protocol}//${location.host}/public/editor/tinymce.js`;
      script.onload = () => {
        // Create editable regions
        [...doc.querySelectorAll('.cms-editable')].map(element => {
          const baseUrl = dirname(this.pageUrl);
          const type = getType(element);
          const hasId = element.hasAttribute('id');

          // Enable outline transitions afer a brief delay to prevent flashing
          setTimeout(() => element.classList.add('cms-initialized'), 250);

          // Check for unsupported regions
          if (type === 'unsupported') {
            this.erroneousRegions.push({
              element,
              text: this.$t('Unsupported Element')
            });
            return;
          }

          // Check for the required id attribute
          if (!hasId) {
            this.erroneousRegions.push({
              element,
              text: this.$t('Missing ID')
            });
            return;
          }

          // Block regions
          if (type === 'block') {
            const allowDrop = true;

            //
            // IE doesn't support content editable on certain elements (e.g. <td>, <th>) and TinyMCE
            // doesn't work with <li> elements.
            //
            // See: http://msdn.microsoft.com/en-us/library/ms537837%28v=VS.85%29.aspx
            //      https://github.com/tinymce/tinymce/issues/4934
            //
            // We work around this by shimming the region with an editable <div> and initializing
            // TinyMCE on that instead. Pages will still publish properly, since we serialize the
            // innerHTML of the editor, not the original element.
            //
            const requiresShim = ['td', 'th', 'li'].includes(element.tagName.toLowerCase());
            const shim = requiresShim ? doc.createElement('div') : null;
            if (requiresShim) {
              shim.setAttribute('data-cms-shim', true);
              shim.classList.add('cms-editable');
              setTimeout(shim.classList.add('cms-initialized'), 250);

              // Add the shim to the content region and move all its child elements into the shim
              element.appendChild(shim);
              while (element.firstChild !== shim) shim.appendChild(element.firstChild);
            }

            const editor = new Editor(shim || element, { allowDrop, baseUrl }, win.tinymce);

            // Initialize the editor
            editor
              .on('focus', () => (this.activeEditor = editor))
              .on('blur', () => (this.activeEditor = null))
              .on('insertAnchor', () => this.$refs.anchorPanel.show({ editor }))
              .on('insertBlock', () => this.$refs.blocksPanel.show({ editor }))
              .on('insertCodeBlock', () => this.$refs.codeBlockPanel.show({ editor }))
              .on('insertEmbed', () => this.$refs.videoPanel.show({ editor }))
              .on('insertLink', () => this.$refs.linkPanel.show({ editor }))
              .on('insertImage', () => this.$refs.imagePanel.show({ editor }))
              .on('insertGallery', () => this.$refs.galleryPanel.show({ editor }))
              .on('insertTable', () => editor.insertTable(2, 3))
              .on('keydown', event => this.handlekeyboardShortcut(event))
              .on('drop', event => {
                // Upload and insert a file when dropped
                const files = event.dataTransfer.files;
                const formData = new FormData();
                if (files.length) {
                  this.isUploading = true;
                  formData.append('path', this.site.uploadFolder);
                  formData.append('files', files[0]);
                  this.$http
                    .post(`/sites/${this.site.id}/files`, formData)
                    .then(res => {
                      const file = res.data.files[0];
                      const description = prettyFilename(file.name);
                      const path = removeBasePath(file.path, this.site.homeFolder);
                      const isImage = !!file.name.match(/\.(gif|jpeg|jpg|png|svg|webp)$/);
                      const url = new URI(this.site.url)
                        .path(URI.joinPaths(this.site.url, path))
                        .relativeTo(this.pageUrl)
                        .toString();
                      if (isImage) {
                        editor.insertImage(url, { description });
                      } else {
                        editor.insertContent(`<a href="${encodeURI(url)}">${escapeHtml(description)}</a>`);
                      }
                    })
                    .catch(err => {
                      this.$notify({
                        type: 'error',
                        title: this.$t('Error'),
                        message: getErrorMessage(err.response.data.code)
                      });
                    })
                    .finally(() => (this.isUploading = false));
                }
              })
              .on('paste', event => {
                const clipboardData = event.clipboardData || window.clipboardData;
                const text = clipboardData.getData('Text');
                const html = clipboardData.getData('text/html');
                if (/^https?:\/\//i.test(text)) {
                  event.preventDefault();
                  this.$http
                    .get('/utilities/oembed', { params: { url: text } })
                    .then(res => {
                      if (res.data.oEmbed && res.data.oEmbed.html) {
                        // We found an oEmbed, let's use it
                        editor.insertEmbed(res.data.oEmbed.html);
                      } else {
                        // No oEmbed found, paste the content as-is
                        editor.insertContent(html);
                      }
                    })
                    // Request error, paste as-is
                    .catch(() => editor.insertContent(html));
                }
              })
              .initialize()
              .then(() => {
                this.contentRegions.push({ editor, element, type });
                this.makeClean();
              });
          }

          // Inline and link regions
          if (type === 'inline' || type === 'link') {
            const editor = new Editor(element, { baseUrl }, win.tinymce);

            // Initialize the editor
            editor
              .on('focus', () => (this.activeEditor = editor))
              .on('blur', () => (this.activeEditor = null))
              .on('insertLink', () => this.$refs.linkPanel.show({ editor }))
              .on('keydown', event => this.handlekeyboardShortcut(event))
              .initialize()
              .then(() => {
                this.contentRegions.push({ editor, element, type });
                this.makeClean();
              });
          }

          // Text regions
          if (type === 'text') {
            const plainText = true;
            const editor = new Editor(element, { baseUrl, plainText }, win.tinymce);

            // Initialize the editor
            editor
              .on('focus', () => (this.activeEditor = editor))
              .on('blur', () => (this.activeEditor = null))
              .initialize()
              .then(() => {
                this.contentRegions.push({ editor, element, type });
                this.makeClean();
              });
          }

          // Image regions
          if (type === 'image') {
            this.contentRegions.push({ element, type });
            this.makeClean();

            // Edit on click
            element.addEventListener('click', event => {
              event.preventDefault();
              this.editImage(element);
            });
          }

          // Background regions
          if (type === 'background') {
            this.contentRegions.push({ element, type });
            this.makeClean();

            // Edit on click
            element.addEventListener('click', event => {
              if (event.target === element) {
                event.preventDefault();
                this.editBackground(element);
              }
            });
          }

          // Gallery regions
          if (type === 'gallery') {
            this.contentRegions.push({ element, type });
            this.makeClean();

            // Edit on click
            element.addEventListener('click', event => {
              event.preventDefault();
              this.editGallery(element);
            });
          }
        });
      };

      // If TinyMCE fails to load
      script.onerror = () => {
        this.$notify({
          type: 'error',
          title: this.$t('Error'),
          message: this.$t('Sorry, but the editor could not be initialized. Please reload the page and try again.'),
          duration: 5000
        });
      };

      doc.body.appendChild(script);
    },

    handleDeletedRevisions(revisions) {
      const deletedIds = revisions.map(r => r.id);
      this.revisions = this.revisions.filter(r => !deletedIds.includes(r.id));
    },

    handleDone() {
      this.$router.push({ name: 'Site', params: { siteId: this.site.id } });
    },

    handlekeyboardShortcut(event) {
      const isMeta = event && (event.ctrlKey || event.metaKey);
      const isShift = event && event.shiftKey;

      // Save draft
      if (isMeta && !isShift && event.key === 's') {
        event.preventDefault();
        this.saveDraft();
      }

      // Publish
      if (isMeta && isShift && event.key === 's') {
        event.preventDefault();
        this.publish();
      }
    },

    isDirty() {
      const state = JSON.stringify({
        metadata: this.metadata,
        regions: this.serializeRegions()
      });

      if (!this.isPageLoading) {
        return state !== this.cleanState;
      } else {
        return false;
      }
    },

    makeClean() {
      this.cleanState = JSON.stringify({
        metadata: this.metadata,
        regions: this.serializeRegions()
      });
    },

    openFileBrowser() {
      this.fileBrowserDialogVisible = true;
      this.$nextTick(() => {
        this.$refs.fileBrowser.open({ path: this.site.homeFolder });
      });
    },

    saveAndRedirect(to) {
      this.upgradeRequiredDialogVisible = false;
      this.saveDraft()
        .then(() => setTimeout(() => this.$router.push(to), 750))
        .catch(() => {
          /* Whoops! Don't go anywhere just yet */
        });
    },

    saveDraft() {
      return new Promise((resolve, reject) => {
        const metadata = this.metadata;
        const regions = this.serializeRegions();
        const isEditingDraft = this.currentRevision && this.currentRevision.isDraft;
        const method = isEditingDraft ? 'put' : 'post';
        const url = isEditingDraft
          ? `/sites/${this.site.id}/pages/${this.page.id}/revisions/${this.currentRevision.id}`
          : `/sites/${this.site.id}/pages/${this.page.id}/revisions`;

        // Can't save a draft when the page is still loading or publishing
        if (this.isPageLoading || this.isPublishing) {
          return Promise.resolve();
        }

        this.publishStatus = null;
        this.publishPercentage = 0;
        this.publishMessage = this.$t('Saving...');
        this.showPublishQueue = false;
        this.isPublishing = true;

        this.$http[method](url, {
          isDraft: true,
          metadata,
          regions
        })
          .then(res => {
            this.currentRevision = res.data.revision;
            this.publishPercentage = 100;
            this.publishStatus = 'success';
            this.publishMessage = this.$t('Finished');
            this.makeClean();
            this.fetchRevisions();
            history.replaceState(
              null,
              null,
              `/sites/${this.site.id}/pages/${this.page.id}/revisions/${this.currentRevision.id}`
            );
            setTimeout(() => {
              this.isPublishing = false;
              resolve(res.data);
            }, 1000);
          })
          .catch(err => {
            this.publishPercentage = 100;
            this.publishStatus = 'exception';
            this.publishMessage = getErrorMessage(err.response.data.code);
            setTimeout(() => {
              this.isPublishing = false;
              reject(err);
            }, 3000);
          });
      });
    },

    publish() {
      const metadata = this.metadata;
      const regions = this.serializeRegions();

      // Can't save a draft when the page is still loading or publishing
      if (this.isPageLoading || this.isPublishing) {
        return Promise.resolve();
      }

      this.publishStatus = null;
      this.publishStatusAttempts = 0;
      this.publishQueue = [];
      this.publishPercentage = 0;
      this.publishMessage = this.$t('Connecting...');
      this.showPublishQueue = false;
      this.isPublishing = true;

      return this.$http
        .post(`/sites/${this.site.id}/pages/${this.page.id}/publish`, {
          metadata,
          regions
        })
        .then(res => {
          // Once the publish operation starts, we'll use the publish key to poll for progress
          return this.pollUntilComplete(res.data.statusId);
        })
        .catch(err => {
          if (err.response.data.code === 'UPGRADE_REQUIRED') {
            // Upgrade is required to publish commercial sites
            this.publishStatus = 'exception';
            this.publishMessage = getErrorMessage(err.response.data.code);
            this.publishPercentage = 100;
            setTimeout(() => (this.isPublishing = false), 3000);

            // Show the upgrade dialog for users who can manage billing
            if (this.$store.state.user.canManageBilling) {
              setTimeout(() => (this.upgradeRequiredDialogVisible = true), 3500);
            }
          } else {
            // All other errors
            this.isPublishing = false;
            this.$notify({
              type: 'error',
              title: this.$t('Error'),
              message: getErrorMessage(err.response.data.code)
            });
          }
        });
    },

    pollUntilComplete(statusId) {
      const pollFrequency = 500;
      return this.$http
        .get(`/sites/${this.site.id}/pages/${this.page.id}/publish/${statusId}`)
        .then(res => {
          const status = res.data.publishStatus;
          this.publishQueue = status.queue;
          this.unpublishedRegions = status.unpublishedRegions;

          if (status.isRequestComplete && status.errorCode) {
            // A publishing error occurred
            this.publishPercentage = 100;
            this.publishStatus = 'exception';
            this.publishMessage = getErrorMessage(status.errorCode);
            setTimeout(() => (this.isPublishing = false), 3000);
          } else if (status.isRequestComplete) {
            // Publishing is complete
            if (this.publishQueue.filter(page => page.errorCode).length) {
              this.publishPercentage = 100;
              this.publishMessage = this.$t('Finished With Errors');
              this.publishStatus = 'exception';
              this.showPublishQueue = true;
            } else {
              this.publishPercentage = 100;
              this.publishMessage = this.$t('Finished');
              this.publishStatus = 'success';
              setTimeout(() => (this.isPublishing = false), 1000);
            }

            history.replaceState(null, null, `/sites/${this.site.id}/pages/${this.page.id}`);
            this.makeClean();

            // If a draft was published, delete the corresponding revision since a historical one
            // was created to replace it.
            Promise.resolve()
              .then(() => {
                if (this.currentRevision && this.currentRevision.isDraft) {
                  return this.$http.delete(
                    `/sites/${this.site.id}/pages/${this.page.id}/revisions/${this.currentRevision.id}`
                  );
                }
              })
              .then(() => (this.currentRevision = null))
              .catch(() => {
                /* ignore errors */
              })
              .finally(() => this.fetchRevisions());
          } else {
            // Publishing is still in progress
            const total = status.total;
            const processed = status.processed;
            const percentage = parseInt((processed / total) * 100);

            // We don't let the percentage hit 100% until the request is actually complete. This
            // prevents the indicator from jumping to 100% before we have a list of include files
            // that need to be updated.
            if (percentage < 100) this.publishPercentage = percentage;

            // Show a message while publishing
            if (total > 1) {
              this.publishMessage = this.$tc('Publishing {processed} of {total} Pages', total, { processed, total });
            } else {
              this.publishMessage = this.$t('Connecting...');
            }

            // Continue polling
            setTimeout(() => this.pollUntilComplete(statusId), pollFrequency);
          }
        })
        .catch(err => {
          // If we receive an error, try again until retries is exhausted
          if (++this.publishStatusAttempts < 3) {
            setTimeout(() => this.pollUntilComplete(statusId), pollFrequency);
          } else {
            this.publishStatus = 'exception';
            this.publishMessages = getErrorMessage(err.response.data.code);
            setTimeout(() => (this.isPublishing = false), 3000);
          }
        });
    },

    // Parses metadata from the editable page's DOM
    parseMetadata() {
      const iframe = this.$refs.pageFrame;
      const doc = iframe.contentWindow.document;
      const metadata = {
        title: doc.querySelector('title') ? doc.querySelector('title').innerText : '',
        description: '',
        image: '',
        robots: false,
        type: 'website',
        url: this.pageUrl
      };

      // Case-insensitive parsing for metadata. Once browser support gets better, we can use the
      // same selector as the backend uses. See https://caniuse.com/#feat=css-case-insensitive
      [...doc.querySelectorAll('meta[name], meta[property]')].map(meta => {
        const name = (meta.name || '').toLowerCase();
        const property = (meta.getAttribute('property') || '').toLowerCase();
        const content = meta.content;

        if (name === 'description') metadata.description = content;
        if (name === 'robots') metadata.robots = /noindex/.test(content);
        if (property === 'og:image') metadata.image = content;
        if (property === 'og:type') metadata.type = content;
        if (property === 'og:url') metadata.url = content;
      });

      return metadata;
    },

    // Makes an absolute URL. If the URL is external, a fully qualified URL will be returned,
    // otherwise the absolute path will be returned.
    getAbsoluteUrl(url) {
      // Skip other protocols
      if (/^(tel|mailto):/i.test(url)) {
        return url;
      }

      const pageUrl = new URI(this.site.url).path(URI.joinPaths(this.site.url, this.page.path));
      const absoluteUrl = new URI(url).absoluteTo(pageUrl);
      return this.isExternalUrl(url) ? absoluteUrl.toString() : absoluteUrl.path().toString();
    },

    // Determines if an arbitrary URL is external to the current site
    isExternalUrl(url) {
      const currentHost = new URI(this.site.url).normalizeHostname().host();
      const uriHost = new URI(url).normalizeHostname().host();
      return currentHost !== uriHost;
    },

    // Converts all href and src attributes to absolute links
    makeAttributesAbsolute(html) {
      const iframe = this.$refs.pageFrame;
      const doc = iframe.contentWindow.document;
      const div = doc.createElement('div');
      div.innerHTML = html;

      [...div.querySelectorAll('[href], [src]')].map(el => {
        if (el.hasAttribute('href')) el.href = this.getAbsoluteUrl(el.getAttribute('href'));
        if (el.hasAttribute('src')) el.src = this.getAbsoluteUrl(el.getAttribute('src'));
      });

      return div.innerHTML;
    },

    // Serializes regions from the DOM
    serializeRegions() {
      const regions = this.contentRegions;
      const data = [];

      regions.map(region => {
        const useAbsoluteUrls = region.element.getAttribute('data-urls') === 'absolute';

        // Block, inline, and text regions
        if (region.type === 'block' || region.type === 'inline' || region.type === 'text') {
          data.push({
            type: region.type,
            id: region.element.getAttribute('id'),
            content: useAbsoluteUrls
              ? this.makeAttributesAbsolute(region.editor.getContent())
              : region.editor.getContent()
          });
        }

        // Link regions
        if (region.type === 'link') {
          data.push({
            type: region.type,
            id: region.element.getAttribute('id'),
            href: useAbsoluteUrls
              ? this.getAbsoluteUrl(region.element.getAttribute('href'))
              : region.element.getAttribute('href'),
            title: region.element.getAttribute('title'),
            target: region.element.getAttribute('target'),
            rel: region.element.getAttribute('rel'),
            content: useAbsoluteUrls
              ? this.makeAttributesAbsolute(region.editor.getContent())
              : region.editor.getContent()
          });
        }

        // Background regions
        if (region.type === 'background') {
          let backgroundImage = region.element.style.backgroundImage;
          if (backgroundImage && useAbsoluteUrls) {
            const url = this.$refs.backgroundPanel.parseCssUrl(backgroundImage);
            backgroundImage = this.$refs.backgroundPanel.createCssUrl(this.getAbsoluteUrl(url));
          }

          data.push({
            type: region.type,
            id: region.element.getAttribute('id'),
            backgroundImage,
            backgroundColor: region.element.style.backgroundColor,
            backgroundSize: region.element.style.backgroundSize,
            backgroundRepeat: region.element.style.backgroundRepeat
          });
        }

        // Image regions
        if (region.type === 'image') {
          data.push({
            type: region.type,
            id: region.element.getAttribute('id'),
            src: useAbsoluteUrls
              ? this.getAbsoluteUrl(region.element.getAttribute('src'))
              : region.element.getAttribute('src'),
            alt: region.element.getAttribute('alt')
          });
        }

        // Gallery regions
        if (region.type === 'gallery') {
          data.push({
            type: region.type,
            id: region.element.getAttribute('id'),
            content: useAbsoluteUrls ? this.makeAttributesAbsolute(region.element.innerHTML) : region.element.innerHTML
          });
        }
      });

      return data;
    },

    updateMetadata(data) {
      this.metadata = Object.assign({}, this.metadata, data);
    },

    openRevision(revision) {
      const open = () => {
        this.currentRevision = revision;
        this.makeClean();
        this.$router.push({
          name: 'EditRevision',
          params: {
            siteId: this.site.id,
            pageId: this.page.id,
            revisionId: revision.id
          }
        });
      };

      if (this.isDirty()) {
        this.$confirm(this.$t("Your changes haven't been saved. Are you sure you want to continue?"), {
          title: this.$t('Unsaved Changes'),
          confirmButtonText: this.$t('Continue'),
          cancelButtonText: this.$t('Cancel')
        }).then(() => open());
      } else {
        open();
      }
    },

    fetchRevisions() {
      this.isLoadingRevisions = true;
      return this.$http
        .get(`/sites/${this.site.id}/pages/${this.page.id}/revisions`, {
          params: {
            limit: 100,
            offset: 0,
            sortBy: 'isDraft'
          }
        })
        .then(res => (this.revisions = res.data.revisions))
        .catch(() => (this.revisions = []))
        .finally(() => (this.isLoadingRevisions = false));
    }
  }
};
</script>

<template>
  <dashboard-layout :disable-scrolling="true" :status-code="statusCode" size="full">
    <!-- Sidebar -->
    <template slot="sidebar">
      <!-- Admin -->
      <sidebar-link-group v-if="$store.state.user.isAdmin" :title="$t('Admin')">
        <sidebar-link
          :text="$t('Edit Source')"
          :to="{ name: 'EditSource', params: { siteId: site.id, pageId: page.id } }"
          icon="el-icon-fa-code"
        />
      </sidebar-link-group>

      <!-- Actions -->
      <sidebar-link-group :title="$t('Actions')">
        <sidebar-link
          :text="$t('Edit Metadata')"
          :badge-text="!isPageLoading && !isMetadataComplete ? '!' : null"
          :disabled="isPageLoading"
          badge-type="error"
          href="#"
          icon="el-icon-fa-info"
          @click="$refs.metadataPanel.show(metadata)"
        />
        <sidebar-link
          :text="$t('Drafts & Revisions')"
          :badge-text="revisions.filter(r => r.isDraft).length || null"
          badge-type="warning"
          href="#"
          icon="el-icon-fa-pen-nib"
          @click="$refs.revisionPanel.show()"
        />
        <sidebar-link :text="$t('Manage Files')" icon="el-icon-fa-folder" @click="openFileBrowser()" />
        <sidebar-link :text="$t('View Webpage')" :href="pageUrl" icon="el-icon-fa-external-link" />
      </sidebar-link-group>

      <sidebar-button-group>
        <!-- Publish -->
        <el-dropdown split-button type="primary" trigger="click" @click="publish">
          {{ $t('Publish') }}
          <el-dropdown-menu slot="dropdown">
            <el-dropdown-item @click.native="saveDraft">
              <i class="el-icon-fa-pen-nib" />
              {{ $t('Save Draft') }}
              <kbd>{{ '[Meta]S' | keyboardShortcut }}</kbd>
            </el-dropdown-item>
            <el-dropdown-item @click.native="publish">
              <i class="el-icon-fa-cloud-upload" />
              {{ $t('Publish') }}
              <kbd>{{ '[Shift][Meta]S' | keyboardShortcut }}</kbd>
            </el-dropdown-item>
          </el-dropdown-menu>
        </el-dropdown>

        <!-- Done -->
        <el-button type="info" @click="handleDone">
          {{ $t('Done') }}
        </el-button>
      </sidebar-button-group>
    </template>

    <!-- Editor -->
    <div v-loading="isPageLoading || isUploading" class="editor">
      <!-- Page frame -->
      <iframe ref="pageFrame" :src="iframeSrc" @load="handlePageLoad" />

      <!-- Toolbars -->
      <editor-toolbar ref="editorToolbar" :editor="activeEditor" />
      <repeatable-toolbar :editor="activeEditor" />
      <table-toolbar :editor="activeEditor" />

      <!-- Guides -->
      <editor-guide
        v-for="(region, index) in erroneousRegions"
        :key="`error-guide-${index}`"
        :element="region.element"
        :has-error="true"
        :icon="region.icon"
        :text="region.text"
        :title="region.title"
        :hidden="activeEditor !== null"
      />
    </div>

    <!-- Panels -->
    <anchor-panel ref="anchorPanel" />
    <background-panel ref="backgroundPanel" :site="site" :page="page" />
    <code-block-panel ref="codeBlockPanel" />
    <gallery-panel ref="galleryPanel" :site="site" :page="page" />
    <link-panel ref="linkPanel" :site="site" :page="page" />
    <image-panel ref="imagePanel" :site="site" :page="page" />
    <video-panel ref="videoPanel" />
    <blocks-panel ref="blocksPanel" :blocks="blocks" />
    <metadata-panel ref="metadataPanel" :site="site" :page="page" @submit="updateMetadata" />
    <revision-panel
      ref="revisionPanel"
      :loading="isLoadingRevisions"
      :current-revision="currentRevision"
      :revisions="revisions"
      :site="site"
      @submit="openRevision"
      @delete="handleDeletedRevisions"
    />

    <!-- File browser -->
    <el-dialog :visible.sync="fileBrowserDialogVisible" :title="$t('Manage Files')" class="manage-files-dialog">
      <file-browser
        ref="fileBrowser"
        :base-path="site.uploadFolder"
        :multiple="true"
        :show-pages="false"
        :show-select="false"
        :site="site"
        type="dialog"
      />
    </el-dialog>

    <!-- Publish progress -->
    <publish-progress
      :visible="isPublishing"
      :status="publishStatus"
      :message="publishMessage"
      :percent-complete="publishPercentage"
      :queue="publishQueue"
      :show-queue="showPublishQueue"
      :unpublished-regions="unpublishedRegions"
      @close="isPublishing = false"
    />

    <!-- Upgrade required dialog -->
    <el-dialog :visible.sync="upgradeRequiredDialogVisible" :title="$t('Upgrade Required')" class="error-dialog">
      <p>
        {{
          $t(
            "This appears to be a commercial website, but you're on a personal plan. Please upgrade your account to publish this website."
          )
        }}
      </p>
      <p>
        {{ $t("We can save your changes as a draft so you can publish them after you've upgraded.") }}
      </p>
      <p>
        <a href="#" @click.prevent="saveAndRedirect({ name: 'Support' })">
          {{ $t('Did we make a mistake? Let us know!') }}
        </a>
      </p>
      <span slot="footer" class="dialog-footer">
        <el-button type="primary" @click="saveAndRedirect({ name: 'Billing' })">
          {{ $t('Save Draft & Upgrade') }}
        </el-button>
      </span>
    </el-dialog>
  </dashboard-layout>
</template>

<style lang="scss" scoped>
@import '@/styles/variables.scss';

.editor {
  position: relative;
  display: flex;
  flex: 1 1 auto;

  iframe {
    flex: 1 1 auto;
    border: none;
    overflow: auto;
  }
}
</style>
